import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Button, IconButton, Chip, Alert, Typography, Menu, MenuItem, Collapse } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MarketWatch from '../Components/MarketWatch';
import { Trades, QuickOrder } from '../Components/Trades';
import { OrderMonitor } from '../Components/OrdersMonitoring';
import Connector from '../Components/Connector';
import Monitoring from '../Components/Monitoring';
import './DashboardPage.css';
import { useSignalRContext } from "../SignalR/useSignalR";
import { useAuth } from "../provider/authProvider";
import { useAccount } from "../provider/accountProvider";
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ManualOrderClose from '../pages/ManualOrderClose';
import SettingsMain from '../Components/Settings/SettingsMain';
import { GetUser } from '../api/auth';
import { getPositionsByAccount } from '../api/positionApi';
import { updateStrategyParameters, executeManualOrder, resetStrategySnapshot, getStrategyById, stopStrategy } from '../api/strategyApi';
import { toast } from 'react-toastify';
// FE-8: Import dashboard type utilities
import { isLegacyDashboard, DASHBOARD_TYPES } from '../utils/dashboardTypeUtils';

// FE-8: Dashboard type constant for this page
const CURRENT_DASHBOARD_TYPE = DASHBOARD_TYPES.LEGACY; // 'Algo.01'

const DashboardPage = () => {
    const navigate = useNavigate();
    const { disconnect, clear, dataSource, isMockMode, mockDataWarning, connectionQuality, lastRealDataUpdate, strategyDashboardData, positionsList, tickData, loadRunningStrategies } = useSignalRContext();
    const { clearToken, hasPermission, isAdmin, getCurrentUser, token } = useAuth();

    // Use centralized account management from AccountProvider
    const { accounts: userAccounts, selectedAccount, setSelectedAccount, getAccountNumber } = useAccount();

    const currentUser = getCurrentUser();
    const userId = currentUser?.id;

    // 🔒 SECURITY FIX: Filter strategies by account and dashboard type
    const [filteredStrategyData, setFilteredStrategyData] = useState(null);

    const getStrategyId = useCallback(() => {
        // 🔒 SECURITY: Use filtered data instead of raw strategyDashboardData
        return filteredStrategyData?.id ||
               filteredStrategyData?.strategyId ||
               filteredStrategyData?.StrategyId ||
               filteredStrategyData?.strategyID;
    }, [filteredStrategyData]);

    const calculateTotalPnL = useCallback(() => {
        if (!positionsList || positionsList.length === 0) {
            return 0;
        }

        const accountNumber = selectedAccount?.accountNumber ||
                             selectedAccount?.account_number ||
                             (typeof selectedAccount === 'number' ? selectedAccount : null);

        if (!accountNumber) {
            return 0;
        }

        const accountPositions = positionsList.filter(pos => {
            const posAccountNumber = pos.accountNumber || pos.AccountNumber;
            return posAccountNumber && posAccountNumber.toString() === accountNumber.toString();
        });


        if (accountPositions.length === 0) {
            return 0;
        }

        let totalPnL = 0;
        let positionsWithQuotes = 0;
        let positionsWithoutQuotes = 0;

        accountPositions.forEach((position) => {
            const symbol = position.symbolName || position.symbol;
            const volume = parseFloat(position.volume || position.Volume);
            const openPrice = parseFloat(position.openPrice || position.entryPrice || position.OpenPrice || position.EntryPrice);
            const side = position.direction || position.tradeSide || position.Direction || position.TradeSide;

            if (!symbol || isNaN(volume) || isNaN(openPrice) || !side) {
                return;
            }

            const quote = tickData?.find(tick => tick.symbol === symbol);
            if (!quote) {
                positionsWithoutQuotes++;
                return;
            }
            positionsWithQuotes++;

            const currentPrice = side === "Buy" ? parseFloat(quote.bid) : parseFloat(quote.ask);
            if (isNaN(currentPrice)) {
                return;
            }

            const contractSize = 100000;
            const symbolDigits = symbol.includes('JPY') ? 3 : 5;

            const priceDiff = side === "Buy"
                ? (currentPrice - openPrice)
                : (openPrice - currentPrice);

            const profitValue = priceDiff * contractSize * volume;
            totalPnL += profitValue;
        });

        return totalPnL;
    }, [positionsList, tickData, selectedAccount]);

    const realTimePnL = useMemo(() => {
        return calculateTotalPnL();
    }, [positionsList, tickData, selectedAccount, calculateTotalPnL]);

    // 🔒 CRITICAL SECURITY FIX: Filter incoming SignalR strategy data by account and dashboard type
    useEffect(() => {
        if (!strategyDashboardData) {
            setFilteredStrategyData(null);
            console.log('[DashboardPage] ⏳ No strategy data yet');
            return;
        }

        // Extract account number from selected account
        const currentAccountNumber = selectedAccount?.accountNumber ||
                                     selectedAccount?.account_number ||
                                     (typeof selectedAccount === 'number' ? selectedAccount : null);

        // Extract account number from strategy data
        const strategyAccountNumber = strategyDashboardData.accountNumber ||
                                      strategyDashboardData.AccountNumber;

        // Extract dashboard type (default to Algo.01 for old strategies)
        const dashboardType = strategyDashboardData.dashboardType || 'Algo.01';

        console.log('[DashboardPage] 🔍 Strategy Filter Check:', {
            strategyId: getStrategyId(),
            strategyAccount: strategyAccountNumber,
            currentAccount: currentAccountNumber,
            dashboardType: dashboardType,
            expectedDashboard: 'Algo.01'
        });

        // FILTER 1: Check dashboard type (Legacy dashboard = Algo.01 OR Algo-1)
        // FE-8: Use utility function for consistent format handling
        const isLegacy = isLegacyDashboard(dashboardType);

        if (!isLegacy) {
            console.log(`[DashboardPage] ⚠️ FILTERED: Strategy assigned to ${dashboardType}, not Legacy Dashboard (Algo.01)`);
            setFilteredStrategyData(null);
            return;
        }

        // FILTER 2: Check account ownership (only if account is selected)
        // During initial load, account may not be selected yet - show data, user will select account
        if (currentAccountNumber && strategyAccountNumber) {
            if (strategyAccountNumber?.toString() !== currentAccountNumber?.toString()) {
                console.log('[DashboardPage] ⚠️ Strategy belongs to different account - filtering');
                console.log('[DashboardPage] Strategy Account:', strategyAccountNumber, 'Selected:', currentAccountNumber);
                setFilteredStrategyData(null);
                return;
            }
        }

        // ✅ ALL CHECKS PASSED - Show strategy
        console.log('[DashboardPage] ✅ Strategy passed all filters - DISPLAYING');
        setFilteredStrategyData(strategyDashboardData);

    }, [strategyDashboardData, selectedAccount, getStrategyId]);

    const [panelStates, setPanelStates] = useState({
        'market-watch': true,
        'monitoring': true,
        'right-panel': true,
        'bottom-panel': true
    });
    
    const [bottomPanelHeight, setBottomPanelHeight] = useState(200);
    const [isResizing, setIsResizing] = useState(false);
    
    const [currentRightPanel, setCurrentRightPanel] = useState('strategy');

    const [selectedSymbol, setSelectedSymbol] = useState(null);

    const [showFileDropdown, setShowFileDropdown] = useState(false);
    const [showViewDropdown, setShowViewDropdown] = useState(false);
    const [showOrderStrategySubmenu, setShowOrderStrategySubmenu] = useState(false);

    // Controls section collapsed by default for clean, professional look
    const [controlsExpanded, setControlsExpanded] = useState(false);

    const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
    const accountMenuOpen = Boolean(accountMenuAnchor);

    const [showSettings, setShowSettings] = useState(false);

    const [showTradePanelModal, setShowTradePanelModal] = useState(false);

    const [showFullPreviewModal, setShowFullPreviewModal] = useState(false);

    const [strategyParams, setStrategyParams] = useState({
        buyGrid: '',
        sellGrid: '',
        buyLegTP: '',
        sellLegTP: '',
        accumulativeTP: '',
        accumulativeSL: '',
        benchmarkThresholdBuy: '',
        benchmarkThresholdSell: ''
    });

    const lastLoadedStrategyIdRef = useRef(null);

    useEffect(() => {
        if (loadRunningStrategies && token) {
            loadRunningStrategies();
        }
    }, [loadRunningStrategies, token]);

    useEffect(() => {
        const fetchStrategyParameters = async () => {
            const currentStrategyId = getStrategyId();

            if (!currentStrategyId) {
                return;
            }

            if (currentStrategyId === lastLoadedStrategyIdRef.current) {
                return;
            }

            if (!token) {
                return;
            }

            try {
                const strategyData = await getStrategyById(token, currentStrategyId);

                if (strategyData?.parameters) {
                    const params = strategyData.parameters;

                    const newParams = {
                        buyGrid: params['GridManagement.BuyGrid'] || params.BuyGrid || params.buyGrid || '',
                        sellGrid: params['GridManagement.SellGrid'] || params.SellGrid || params.sellGrid || '',
                        buyLegTP: params['GridManagement.BuyLegTP'] || params.BuyLegTP || params.buyLegTP || '',
                        sellLegTP: params['GridManagement.SellLegTP'] || params.SellLegTP || params.sellLegTP || '',
                        accumulativeTP: params['GridManagement.AccumulativeTP'] || params.AccumulativeTP || params.accumulativeTP || '',
                        accumulativeSL: params['GridManagement.AccumulativeSL'] || params.AccumulativeSL || params.accumulativeSL || '',
                        benchmarkThresholdBuy: params['GridManagement.BenchmarkThresholdBuy'] || params.BenchmarkThresholdBuy || params.benchmarkThresholdBuy || '',
                        benchmarkThresholdSell: params['GridManagement.BenchmarkThresholdSell'] || params.BenchmarkThresholdSell || params.benchmarkThresholdSell || ''
                    };

                    setStrategyParams(newParams);
                    lastLoadedStrategyIdRef.current = currentStrategyId;
                }
            } catch (error) {
            }
        };

        fetchStrategyParameters();
    // FE-10: Use filteredStrategyData instead of raw strategyDashboardData
    }, [filteredStrategyData?.id, token, getStrategyId]);

    const handleParamChange = useCallback((key, value) => {
        setStrategyParams(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const handleApplyParameters = useCallback(async () => {
        try {
            // FE-10: Use filteredStrategyData for dashboard-filtered strategy
            const strategyId = filteredStrategyData?.id ||
                             filteredStrategyData?.strategyId ||
                             filteredStrategyData?.StrategyId ||
                             filteredStrategyData?.strategyID;

            if (!strategyId) {
                toast.error('No strategy ID found', {
                    autoClose: 5000
                });
                return;
            }

            const parameters = {
                "GridManagement.BuyGrid": parseFloat(strategyParams.buyGrid) || 0,
                "GridManagement.SellGrid": parseFloat(strategyParams.sellGrid) || 0,
                "GridManagement.BuyLegTP": parseFloat(strategyParams.buyLegTP) || 0,
                "GridManagement.SellLegTP": parseFloat(strategyParams.sellLegTP) || 0,
                "GridManagement.AccumulativeTP": parseFloat(strategyParams.accumulativeTP) || 0,
                "GridManagement.AccumulativeSL": parseFloat(strategyParams.accumulativeSL) || 0,
                "GridManagement.BenchmarkThresholdBuy": parseFloat(strategyParams.benchmarkThresholdBuy) || 0,
                "GridManagement.BenchmarkThresholdSell": parseFloat(strategyParams.benchmarkThresholdSell) || 0
            };

            const result = await updateStrategyParameters(token, strategyId, parameters);

            if (result.success) {
                toast.success('Parameters updated', {
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.error(error?.message || 'Update failed', {
                autoClose: 5000
            });
        }
    // FE-10: Use filteredStrategyData
    }, [filteredStrategyData, strategyParams, token]);

    const handleBuySpread = useCallback(async () => {
        try {
            const strategyId = getStrategyId();
            // FE-10: Use filteredStrategyData for dashboard-filtered strategy
            const futuresSymbol = filteredStrategyData?.marketData?.futuresSymbol;
            const spotSymbol = filteredStrategyData?.marketData?.spotSymbol;
            const buyLotSize = parseFloat(filteredStrategyData?.parameters?.['GridManagement.BuyBaseLotSize']) || 0.01;

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Buy",
                    quantity: buyLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Sell",
                    quantity: buyLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenBuySpread", accountNumber, orders);

            if (result.success) {
                toast.success('Buy Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to execute Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    // FE-10: Use filteredStrategyData
    }, [filteredStrategyData, token, getStrategyId, selectedAccount]);

    const handleSellSpread = useCallback(async () => {
        try {
            const strategyId = getStrategyId();
            // FE-10: Use filteredStrategyData for dashboard-filtered strategy
            const futuresSymbol = filteredStrategyData?.marketData?.futuresSymbol;
            const spotSymbol = filteredStrategyData?.marketData?.spotSymbol;
            const sellLotSize = parseFloat(filteredStrategyData?.parameters?.['GridManagement.SellBaseLotSize']) || 0.01;

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            if (!futuresSymbol || !spotSymbol) {
                toast.error('Symbol information not available', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const orders = [
                {
                    symbolName: futuresSymbol,
                    tradeSide: "Sell",
                    quantity: sellLotSize
                },
                {
                    symbolName: spotSymbol,
                    tradeSide: "Buy",
                    quantity: sellLotSize
                }
            ];

            const result = await executeManualOrder(token, strategyId, "OpenSellSpread", accountNumber, orders);

            if (result.success) {
                toast.success('Sell Spread opened successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to open Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to execute Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    // FE-10: Use filteredStrategyData
    }, [filteredStrategyData, token, getStrategyId, selectedAccount]);

    const handleCloseBuy = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastBuySpread", accountNumber);

            if (result.success) {
                toast.success('Last Buy Spread closed successfully', { position: "top-right", autoClose: 2000 });

                let pollAttempts = 0;
                const maxAttempts = 10;

                const pollInterval = setInterval(async () => {
                    try {
                        pollAttempts++;

                        const positions = await getPositionsByAccount(token, accountNumber);
                        const buyPositions = positions?.filter(p => p.direction === 'Buy' || p.tradeSide === 'Buy') || [];

                        if (buyPositions.length === 0 || pollAttempts >= maxAttempts) {
                            clearInterval(pollInterval);
                            if (buyPositions.length === 0) {
                            }
                        }
                    } catch (error) {
                        clearInterval(pollInterval);
                    }
                }, 500);
            } else {
                toast.error(result.error || 'Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close Buy Spread', { position: "top-right", autoClose: 2000 });
        }
    // FE-10: Use filteredStrategyData for dependency (matches getStrategyId source)
    }, [filteredStrategyData, token, getStrategyId, selectedAccount]);

    const handleCloseSell = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseLastSellSpread", accountNumber);

            if (result.success) {
                toast.success('Last Sell Spread closed successfully', { position: "top-right", autoClose: 2000 });

                let pollAttempts = 0;
                const maxAttempts = 10;

                const pollInterval = setInterval(async () => {
                    try {
                        pollAttempts++;

                        const positions = await getPositionsByAccount(token, accountNumber);
                        const sellPositions = positions?.filter(p => p.direction === 'Sell' || p.tradeSide === 'Sell') || [];

                        if (sellPositions.length === 0 || pollAttempts >= maxAttempts) {
                            clearInterval(pollInterval);
                            if (sellPositions.length === 0) {
                            }
                        }
                    } catch (error) {
                        clearInterval(pollInterval);
                    }
                }, 500);
            } else {
                toast.error(result.error || 'Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close Sell Spread', { position: "top-right", autoClose: 2000 });
        }
    // FE-10: Use filteredStrategyData for dependency (matches getStrategyId source)
    }, [filteredStrategyData, token, getStrategyId, selectedAccount]);

    const handleCloseAll = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            const accountNumber = selectedAccount?.accountNumber ||
                                 selectedAccount?.account_number ||
                                 (typeof selectedAccount === 'number' ? selectedAccount : null);

            if (!accountNumber) {
                toast.error('No account selected', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await executeManualOrder(token, strategyId, "CloseAll", accountNumber);

            if (result.success) {
                toast.success('All positions closed successfully', { position: "top-right", autoClose: 2000 });

                let pollAttempts = 0;
                const maxAttempts = 10;

                const pollInterval = setInterval(async () => {
                    try {
                        pollAttempts++;

                        const positions = await getPositionsByAccount(token, accountNumber);

                        if (!positions || positions.length === 0 || pollAttempts >= maxAttempts) {
                            clearInterval(pollInterval);
                            if (positions && positions.length === 0) {
                            } else if (pollAttempts >= maxAttempts) {
                            }
                        }
                    } catch (error) {
                        clearInterval(pollInterval);
                    }
                }, 500);
            } else {
                toast.error(result.error || 'Failed to close all positions', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to close all positions', { position: "top-right", autoClose: 2000 });
        }
    // FE-10: Use filteredStrategyData for dependency (matches getStrategyId source)
    }, [filteredStrategyData, token, getStrategyId, selectedAccount]);

    const handleResetStrategy = useCallback(async () => {
        try {
            const strategyId = getStrategyId();

            if (!strategyId) {
                toast.error('No strategy running', { position: "top-right", autoClose: 2000 });
                return;
            }

            const result = await resetStrategySnapshot(token, strategyId);

            if (result.success) {
                toast.success('Strategy snapshot reset successfully', { position: "top-right", autoClose: 2000 });
            } else {
                toast.error(result.error || 'Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
            }
        } catch (error) {
            toast.error('Failed to reset strategy snapshot', { position: "top-right", autoClose: 2000 });
        }
    }, [token, getStrategyId]);

    const handleSymbolSelect = (symbol) => {
        setSelectedSymbol(symbol);
    };

    const [collapsed, setCollapsed] = useState({
        'market-watch': false,
        'monitoring': false,
        'right-panel': false,
        'bottom-panel': false,
        'chart': false
    });

    const getGlobalConnectionStatus = () => {
        if (dataSource === "real") {
            return {
                icon: <CheckCircleIcon />,
                color: "#22c55e",
                text: "LIVE DATA",
                bgColor: "rgba(34, 197, 94, 0.1)",
                borderColor: "#22c55e",
                dotClass: "connected"
            };
        } else if (dataSource === "mock") {
            return {
                icon: <WarningIcon />,
                color: "#f59e0b",
                text: "CACHED DATA",
                bgColor: "rgba(245, 158, 11, 0.1)",
                borderColor: "#f59e0b",
                dotClass: "cached"
            };
        } else {
            return {
                icon: <SignalWifiOffIcon />,
                color: "#ef4444",
                text: "DISCONNECTED",
                bgColor: "rgba(239, 68, 68, 0.1)",
                borderColor: "#ef4444",
                dotClass: "disconnected"
            };
        }
    };

    const connectionStatus = getGlobalConnectionStatus();

    const handleCollapse = (panelId) => {
        setCollapsed(prev => ({ ...prev, [panelId]: !prev[panelId] }));
    };

    const closePanel = (panelId) => {
        setPanelStates(prev => ({ ...prev, [panelId]: false }));
    };

    const togglePanel = (panelId) => {
        setPanelStates(prev => ({ ...prev, [panelId]: !prev[panelId] }));
    };

    const showRightPanel = (type) => {
        if (!panelStates['right-panel']) {
            setPanelStates(prev => ({ ...prev, 'right-panel': true }));
        }
        setCurrentRightPanel(type);
    };

    const handleSettingsClick = () => {
        setShowSettings(true);
    };

    const handleAccountMenuOpen = (event) => {
        setAccountMenuAnchor(event.currentTarget);
    };

    const handleAccountMenuClose = () => {
        setAccountMenuAnchor(null);
    };

    const handleAccountSelect = async (accountNumber) => {
        try {
            const currentStrategyId = getStrategyId();

            if (currentStrategyId) {
                const result = await stopStrategy(token, currentStrategyId);

                if (result.success) {
                    toast.success('Strategy stopped automatically on account switch', {
                        position: "top-right",
                        autoClose: 2000
                    });
                } else {
                    toast.warning('Could not stop strategy automatically', {
                        position: "top-right",
                        autoClose: 2000
                    });
                }
            }

            const account = userAccounts.find(acc => acc.accountNumber === accountNumber);
            if (account) {
                setSelectedAccount(account);
            }
            handleAccountMenuClose();
        } catch (error) {
            toast.error('Error switching account', {
                position: "top-right",
                autoClose: 2000
            });
        }
    };

    const handleTradePanelExpand = () => {
        setShowTradePanelModal(true);
    };

    const getDecimalPrecision = useCallback((symbol) => {
        if (!symbol) return 5; // Default to 5 for forex

        const symbolUpper = symbol.toUpperCase();

        if (symbolUpper.includes('XAU') || symbolUpper.includes('GOLD') ||
            symbolUpper.includes('XAG') || symbolUpper.includes('SILVER') ||
            symbolUpper.includes('XPT') || symbolUpper.includes('XPD')) {
            return 2;
        }

        if (symbolUpper.includes('JPY')) {
            return 3;
        }

        if (symbolUpper.length === 6 && /^[A-Z]{6}$/.test(symbolUpper)) {
            return 5;
        }

        if (symbolUpper.includes('BTC') || symbolUpper.includes('ETH') ||
            symbolUpper.includes('USD') || symbolUpper.includes('EUR')) {
            return 2;
        }

        return 2;
    }, []);

    const formatWithPrecision = useCallback((value, symbol) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        const precision = getDecimalPrecision(symbol);
        return Number(value).toFixed(precision);
    }, [getDecimalPrecision]);

    const layout = [
        { i: 'market-watch', x: 0, y: 0, w: 2, h: 8, minW: 2, minH: 4, maxW: 3 },
        { i: 'chart', x: 2, y: 0, w: 6, h: 8, minW: 4, minH: 6 },
        { i: 'right-panel', x: 8, y: 0, w: 2, h: 8, minW: 2, minH: 4, maxW: 3 },
        { i: 'bottom-panel', x: 0, y: 8, w: 10, h: 4, minH: 3 }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container') && 
                !event.target.closest('.trading-tabs-container') && 
                !event.target.closest('.header-tabs') &&
                !event.target.closest('.MuiButton-root')) {
                setShowFileDropdown(false);
                setShowViewDropdown(false);
                setShowOrderStrategySubmenu(false);
            }
        };

        window.closeAllDropdowns = () => {
            setShowFileDropdown(false);
            setShowViewDropdown(false);
            setShowOrderStrategySubmenu(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            delete window.closeAllDropdowns;
        };
    }, []);

    // FE-10: Use filteredStrategyData for UI display (dashboard-filtered data)
    const memoizedMarketData = useMemo(() => filteredStrategyData?.marketData, [filteredStrategyData?.marketData]);
    const memoizedPerformance = useMemo(() => filteredStrategyData?.performance, [filteredStrategyData?.performance]);
    const memoizedPosition = useMemo(() => filteredStrategyData?.position, [filteredStrategyData?.position]);

    const StrategyScreen = useMemo(() => {
        // FE-10: Use filteredStrategyData for UI display
        const data = filteredStrategyData;
        const marketData = memoizedMarketData;
        const performance = memoizedPerformance;
        const position = memoizedPosition;

        const primarySymbol = marketData?.futuresSymbol || marketData?.spotSymbol || '';

        return (
            <div className="strategy-container-2col">
                <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div className="spread-display">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', marginBottom: '3px' }}>
                                SPREAD MONITOR
                            </div>
                            <div style={{ marginBottom: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <span style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '10px' }}>
                                    Raw Spread:
                                </span>
                                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', fontWeight: 'bold', fontFamily: 'Courier New, monospace' }}>
                                    {formatWithPrecision(marketData?.rawSpread, primarySymbol)}
                                </span>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(107, 114, 128, 0.2)', paddingTop: '3px', marginBottom: '3px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '2px' }}>
                                    <span style={{ color: '#06b6d4', fontSize: '10px' }}>🔷</span>
                                    <span style={{ color: '#06b6d4', fontSize: '10px', fontWeight: '600' }}>Beta Spread</span>
                                </div>
                                <div style={{ color: '#06b6d4', fontSize: '16px', fontWeight: 'bold', fontFamily: 'Courier New, monospace' }}>
                                    {formatWithPrecision(marketData?.betaSpread, primarySymbol)}
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(107, 114, 128, 0.2)', paddingTop: '3px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '8px', marginBottom: '1px' }}>
                                        BETA HIGH
                                    </div>
                                    <div style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(marketData?.upperBand, primarySymbol)}
                                    </div>
                                </div>
                                <div style={{ color: 'rgba(107, 114, 128, 0.5)', fontSize: '12px' }}>|</div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: 'rgba(156, 163, 175, 0.7)', fontSize: '8px', marginBottom: '1px' }}>
                                        BETA LOW
                                    </div>
                                    <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: '600', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(marketData?.lowerBand, primarySymbol)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls Header (Collapsible) */}
                    <div
                        onClick={() => setControlsExpanded(!controlsExpanded)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            padding: '0 8px',
                            backgroundColor: '#141414',
                            borderRadius: '4px',
                            border: '1px solid #262626',
                            marginBottom: '4px',
                            height: '28px'
                        }}
                    >
                        <span style={{
                            color: '#ffffff',
                            fontSize: '9px',
                            fontWeight: '700',
                            letterSpacing: '0.4px',
                            textTransform: 'uppercase',
                            lineHeight: '28px'
                        }}>
                            CONTROLS
                        </span>
                        <IconButton
                            size="small"
                            sx={{
                                color: '#ffffff',
                                padding: '2px',
                                transform: controlsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }}
                        >
                            <ExpandMoreIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </div>

                    {/* Controls Content (Collapsible) */}
                    <Collapse in={controlsExpanded} timeout="auto" unmountOnExit>
                        <div className="strategy-buttons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                            <button
                                type="button"
                                className="strategy-btn buy-spread"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleBuySpread();
                                }}
                                disabled={!getStrategyId()}
                            >
                                BUY SPREAD
                            </button>
                            <button
                                type="button"
                                className="strategy-btn sell-spread"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSellSpread();
                                }}
                                disabled={!getStrategyId()}
                            >
                                SELL SPREAD
                            </button>
                            <button
                                type="button"
                                className="strategy-btn close-buy"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCloseBuy();
                                }}
                                disabled={!getStrategyId()}
                            >
                                CLOSE BUY
                            </button>
                            <button
                                type="button"
                                className="strategy-btn close-sell"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCloseSell();
                                }}
                                disabled={!getStrategyId()}
                            >
                                CLOSE SELL
                            </button>
                            <button
                                type="button"
                                className="strategy-btn close-all"
                                style={{ gridColumn: '1 / -1' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCloseAll();
                                }}
                                disabled={!getStrategyId()}
                            >
                                CLOSE ALL
                            </button>
                            <button
                                type="button"
                                className="strategy-btn reset-strategy"
                                style={{ gridColumn: '1 / -1' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleResetStrategy();
                                }}
                                disabled={!getStrategyId()}
                            >
                                RESET STRATEGY
                            </button>
                        </div>
                    </Collapse>

                    <div className="strategy-inputs-grid">
                        {[
                            { label: 'Benchmark Threshold Buy', key: 'benchmarkThresholdBuy' },
                            { label: 'Benchmark Threshold Sell', key: 'benchmarkThresholdSell' },
                            { label: 'Buy Grid', key: 'buyGrid' },
                            { label: 'Sell Grid', key: 'sellGrid' },
                            { label: 'Buy Leg TP', key: 'buyLegTP' },
                            { label: 'Sell Leg TP', key: 'sellLegTP' },
                            { label: 'Accumulative TP', key: 'accumulativeTP' },
                            { label: 'Accumulative SL', key: 'accumulativeSL' }
                        ].map((field, index) => (
                            <div key={index} className="strategy-input-group">
                                <label className="strategy-label">{field.label}</label>
                                <input
                                    type="number"
                                    step="0.001"
                                    value={strategyParams[field.key] || ''}
                                    onChange={(e) => handleParamChange(field.key, e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    className="strategy-input"
                                    style={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', cursor: 'text' }}
                                />
                            </div>
                        ))}
                    </div>

                    <button className="strategy-apply-btn" onClick={handleApplyParameters}>Apply</button>
                </div>

            <div style={{ padding: '8px' }}>
                <div className="strategy-info-section">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '6px', padding: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(107, 114, 128, 0.3)' }}>
                                <span style={{ fontSize: '16px' }}>📊</span>
                                <span style={{ color: 'rgba(156, 163, 175, 1)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px' }}>MARKET DATA</span>
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{marketData?.futuresSymbol || '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{marketData?.spotSymbol || '-'}</span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures Bid:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.futuresBid, marketData?.futuresSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Futures Ask:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.futuresAsk, marketData?.futuresSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot Bid:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.spotBid, marketData?.spotSymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Spot Ask:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{formatWithPrecision(marketData?.spotAsk, marketData?.spotSymbol)}</span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Raw Spread:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.rawSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Beta Spread:</span>
                                    <span style={{ color: '#06b6d4', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #06b6d4, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.betaSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Upper Band:</span>
                                    <span style={{ color: '#10b981', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #10b981, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.upperBand, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Lower Band:</span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px #ef4444, 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.lowerBand, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Median:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.medianSpread, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Benchmark:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.buySpreadBenchmark, primarySymbol)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Benchmark:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: 'bold', textShadow: '0 0 1px rgba(255, 255, 255, 0.8), 0.5px 0 0 currentColor' }}>{formatWithPrecision(marketData?.sellSpreadBenchmark, primarySymbol)}</span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Indicator:</span>
                                    <span style={{ color: marketData?.indicatorReady ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>{marketData?.indicatorReady ? '✓' : '✗'}</span> {marketData?.indicatorReady ? 'Ready' : 'Not Ready'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Updated:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {marketData?.lastUpdate ? new Date(marketData.lastUpdate).toLocaleTimeString() : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '6px', padding: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(107, 114, 128, 0.3)' }}>
                                <span style={{ fontSize: '16px' }}>💰</span>
                                <span style={{ color: 'rgba(156, 163, 175, 1)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px' }}>PERFORMANCE</span>
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Total P&L:</span>
                                    <span style={{ color: realTimePnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {realTimePnL !== undefined ? `${realTimePnL >= 0 ? '+$' : '-$'}${Math.abs(realTimePnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Realized:</span>
                                    <span style={{ color: performance?.realizedPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.realizedPnL !== undefined ? `${performance.realizedPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.realizedPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Unrealized:</span>
                                    <span style={{ color: performance?.unrealizedPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.unrealizedPnL !== undefined ? `${performance.unrealizedPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.unrealizedPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>LastBuySpreadLegPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.lastBuySpreadLegPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.lastBuySpreadLegPnL !== undefined ? `${performance.pnLBreakdown.lastBuySpreadLegPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.lastBuySpreadLegPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>LastSellSpreadLegPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.lastSellSpreadLegPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.lastSellSpreadLegPnL !== undefined ? `${performance.pnLBreakdown.lastSellSpreadLegPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.lastSellSpreadLegPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>BuySpreadTotalPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.buySpreadTotalPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.buySpreadTotalPnL !== undefined ? `${performance.pnLBreakdown.buySpreadTotalPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.buySpreadTotalPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>SellSpreadTotalPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.sellSpreadTotalPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.sellSpreadTotalPnL !== undefined ? `${performance.pnLBreakdown.sellSpreadTotalPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.sellSpreadTotalPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>netPnL:</span>
                                    <span style={{ color: performance?.pnLBreakdown?.netPnL >= 0 ? '#10b981' : '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {performance?.pnLBreakdown?.netPnL !== undefined ? `${performance.pnLBreakdown.netPnL >= 0 ? '+$' : '-$'}${Math.abs(performance.pnLBreakdown.netPnL).toFixed(2)}` : '-'}
                                    </span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Spread Vol:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.buySpreadLotSize?.toFixed(2) ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Spread Vol:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.sellSpreadLotSize?.toFixed(2) ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Net Spread Vol:</span>
                                    <span style={{
                                        color: position?.netExposure > 0 ? '#10b981' : position?.netExposure < 0 ? '#ef4444' : 'rgba(255, 255, 255, 0.9)',
                                        fontSize: '12px',
                                        fontFamily: 'Courier New, monospace',
                                        fontWeight: 'bold',
                                        textShadow: '0 0 1px currentColor, 0.5px 0 0 currentColor'
                                    }}>
                                        {position?.netExposure?.toFixed(2) ?? '-'}
                                    </span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Active Legs:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.activeLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Buy Legs:</span>
                                    <span style={{ color: '#10b981', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>{position?.buySpreadLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Sell Legs:</span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>{position?.sellSpreadLegs ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Total Lot:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>{position?.totalLotSize?.toFixed(2) || '-'}</span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                                    <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>BUY ENTRIES</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>First Buy@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.buySpreadFirstLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Last Buy@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.buySpreadLastLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        Next Buy@ <span style={{ fontSize: '12px' }}>⬇️</span>:
                                    </span>
                                    <span style={{ color: '#06b6d4', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {formatWithPrecision(position?.buySpreadNextLegEntry, primarySymbol)}
                                    </span>
                                </div>
                            </div>

                            <div style={{ height: '1px', background: 'rgba(107, 114, 128, 0.3)', marginBottom: '8px' }}></div>

                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span>
                                    <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>SELL ENTRIES</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>First Sell@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.sellSpreadFirstLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', lineHeight: '1.5', marginBottom: '3px' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px' }}>Last Sell@:</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px', fontFamily: 'Courier New, monospace' }}>
                                        {formatWithPrecision(position?.sellSpreadLastLegEntry, primarySymbol)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', lineHeight: '1.5' }}>
                                    <span style={{ color: 'rgba(156, 163, 175, 0.8)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        Next Sell@ <span style={{ fontSize: '12px' }}>⬆️</span>:
                                    </span>
                                    <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: 'Courier New, monospace', fontWeight: '600' }}>
                                        {formatWithPrecision(position?.sellSpreadNextLegEntry, primarySymbol)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ background: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '4px', padding: '6px', marginTop: '8px' }}>
                        <div style={{ color: '#06b6d4', fontSize: '9px', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>
                            BETA SPREAD CHART
                        </div>
                        <svg width="100%" height="50" viewBox="0 0 300 50" style={{ background: 'rgba(0, 0, 0, 0.2)', borderRadius: '3px' }}>
                            {[...Array(3)].map((_, i) => (
                                <line key={`h-${i}`} x1={15} x2={285} y1={5 + i * 13} y2={5 + i * 13} stroke="rgba(107, 114, 128, 0.15)" strokeWidth={0.5} />
                            ))}

                            <path
                                d="M 15,30 L 45,27 L 75,28 L 105,24 L 135,22 L 165,25 L 195,20 L 225,18 L 255,16 L 285,14"
                                stroke="#10b981"
                                strokeWidth={1.2}
                                fill="none"
                            />

                            <path
                                d="M 15,30 L 45,27 L 75,28 L 105,24 L 135,22 L 165,25 L 195,20 L 225,18 L 255,16 L 285,14 L 285,39 L 15,39 Z"
                                fill="url(#equityGradientSmall)"
                                opacity={0.3}
                            />

                            <defs>
                                <linearGradient id="equityGradientSmall" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.8 }} />
                                    <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0 }} />
                                </linearGradient>
                            </defs>

                            <circle cx="285" cy="14" r="1.5" fill="#10b981" />
                        </svg>

                        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '2px', fontSize: '6px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Start: <span style={{ color: '#fff', fontWeight: '600' }}>$5k</span></div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Current: <span style={{ color: '#10b981', fontWeight: '600' }}>$12.4k</span></div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#9ca3af' }}>Return: <span style={{ color: '#10b981', fontWeight: '600' }}>+149%</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        );
    }, [memoizedMarketData, memoizedPerformance, memoizedPosition, strategyParams, handleParamChange, handleApplyParameters, handleBuySpread, handleSellSpread, handleCloseBuy, handleCloseSell, handleCloseAll, handleResetStrategy, formatWithPrecision]);

    const logout = () => {
        setSelectedAccount(null); // AccountProvider handles localStorage cleanup

        disconnect();
        clearToken();
        clear();
        navigate("/", { replace: true });
    };

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isResizing) return;
        
        const dashboardContainer = document.querySelector('.dashboard-container');
        const rect = dashboardContainer.getBoundingClientRect();
        const newHeight = rect.bottom - e.clientY;
        
        const constrainedHeight = Math.max(150, Math.min(600, newHeight));
        setBottomPanelHeight(constrainedHeight);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const [manualCloseOpen, setManualCloseOpen] = useState(false);
    const submenuHideTimeout = useRef();

    return (
        <div className="dashboard-container">
            <header className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', background: '#181f2c', minHeight: 38, height: 58, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
                        <span className="brand-title-row" style={{ fontWeight: 900, fontSize: 34, color: '#3deaff', letterSpacing: 0.5, lineHeight: 1, display: 'block' }}>
                            Q<span style={{ fontWeight: 700, fontSize: 35, color: '#3deaff', marginLeft: 0 }}>TradeView</span>
                        </span>
                        <span style={{ fontSize: 11, color: '#bfc9db', fontWeight: 700,fontStyle:'italic', textTransform:'uppercase',display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
                            High-Frequency Trading Engine
                            <span style={{ color: '#FFD600', fontSize: 9, marginLeft: 2 }}>★</span>
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {isAdmin() && (
                        <Button 
                            size="small"
                            variant="contained" 
                            onClick={() => navigate('/adminPanel')}
                            sx={{ 
                                backgroundColor: '#7c3aed',
                                color: 'white',
                                borderRadius: 1.5, 
                                px: 2, 
                                py: 0, 
                                fontWeight: 600, 
                                fontSize: 11, 
                                minHeight: 24, 
                                height: 24, 
                                textTransform: 'none', 
                                boxShadow: 'none', 
                                minWidth: 0, 
                                padding: '4px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:hover': {
                                    backgroundColor: '#6d28d9'
                                }
                            }}
                            title="Access Admin Panel"
                        >
                            👑 Admin Panel
                        </Button>
                    )}
                    
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            color: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722',
                            borderColor: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722',
                            borderRadius: 1.5,
                            px: 0,
                            py: 0,
                            fontWeight: 500,
                            fontSize: 11,
                            minHeight: 24,
                            height: 24,
                            textTransform: 'none',
                            background: connectionStatus.dotClass === 'connected' ? 'rgba(0,255,136,0.04)' : connectionStatus.dotClass === 'connecting' ? 'rgba(255,193,7,0.04)' : 'rgba(255,87,34,0.04)',
                            boxShadow: 'none',
                            minWidth: 0,
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <div className={`status-dot ${connectionStatus.dotClass}`} style={{ width: 4, height: 4, borderRadius: '50%', background: connectionStatus.dotClass === 'connected' ? '#00ff88' : connectionStatus.dotClass === 'connecting' ? '#ffc107' : '#ff5722', boxShadow: connectionStatus.dotClass === 'connected' ? '0 0 3px rgba(0,255,136,0.8)' : connectionStatus.dotClass === 'connecting' ? '0 0 3px rgba(255,193,7,0.8)' : '0 0 3px rgba(255,87,34,0.8)' }}></div>
                        {connectionStatus.dotClass === 'connected' ? 'CONNECTED' : connectionStatus.dotClass === 'connecting' ? 'CONNECTING' : 'DISCONNECTED'}
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAccountMenuOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
                        startIcon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            color: '#06b6d4',
                            borderColor: '#06b6d4',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 600,
                            fontSize: 11,
                            minHeight: 24,
                            height: 24,
                            textTransform: 'none',
                            background: 'rgba(6, 182, 212, 0.08)',
                            boxShadow: 'none',
                            '&:hover': {
                                background: 'rgba(6, 182, 212, 0.15)',
                                borderColor: '#0891b2'
                            }
                        }}
                    >
                        {selectedAccount ? `A/C: ${selectedAccount.accountNumber || selectedAccount}` : 'Select Account'}
                    </Button>

                    <Menu
                        anchorEl={accountMenuAnchor}
                        open={accountMenuOpen}
                        onClose={handleAccountMenuClose}
                        PaperProps={{
                            sx: {
                                background: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: 2,
                                mt: 0.5,
                                minWidth: 200,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                            }
                        }}
                    >
                        {userAccounts.length === 0 ? (
                            <MenuItem disabled sx={{ color: '#94a3b8', fontSize: 12 }}>
                                No accounts available
                            </MenuItem>
                        ) : (
                            userAccounts.map((account) => (
                                <MenuItem
                                    key={account.accountNumber}
                                    onClick={() => handleAccountSelect(account.accountNumber)}
                                    selected={selectedAccount?.accountNumber === account.accountNumber || selectedAccount === account.accountNumber}
                                    sx={{
                                        color: '#e2e8f0',
                                        fontSize: 12,
                                        py: 1.5,
                                        '&:hover': {
                                            background: 'rgba(6, 182, 212, 0.15)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(6, 182, 212, 0.2)',
                                            '&:hover': {
                                                background: 'rgba(6, 182, 212, 0.25)'
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#06b6d4' }}>
                                                {account.accountNumber}
                                            </Typography>
                                            <Chip
                                                label={account.accountType === 0 ? 'DEMO' : 'LIVE'}
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    background: account.accountType === 0 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                                                    color: account.accountType === 0 ? '#f59e0b' : '#22c55e',
                                                    border: `1px solid ${account.accountType === 0 ? '#f59e0b' : '#22c55e'}`
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                                            Balance: ${account.balance?.toFixed(2) || '0.00'} {account.currency || 'USD'}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Menu>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', marginRight: 6, height: 24 }}>
                        <span style={{ fontWeight: 700, color: '#fff', fontSize: 12, lineHeight: 1 }}>
                            {currentUser?.firstName && currentUser?.lastName 
                                ? `${currentUser.firstName} ${currentUser.lastName}` 
                                : currentUser?.email || 'User'
                            }
                        </span>
                        <span style={{ fontSize: 9, color: '#bfc9db', fontWeight: 600, lineHeight: 1 }}>
                            {currentUser?.role === 'Admin' ? 'ADMIN ACCOUNT' : 'PRO ACCOUNT'}
                        </span>
                    </div>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #7b61ff 0%, #a890fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#fff', marginRight: 6 }}>
                        {currentUser?.firstName && currentUser?.lastName 
                            ? `${currentUser.firstName.charAt(0)}${currentUser.lastName.charAt(0)}`.toUpperCase()
                            : currentUser?.email 
                                ? currentUser.email.charAt(0).toUpperCase()
                                : 'U'
                        }
                    </div>
                    <Button variant="outlined" sx={{ color: '#1ca7c7', borderColor: '#1ca7c7', borderRadius: 1.5, px: 0, py: 0, fontWeight: 500, fontSize: 11, minHeight: 24, height: 24, textTransform: 'none', background: 'rgba(0,207,255,0.04)', boxShadow: 'none', minWidth: 0, padding: '4px 8px' }}>Help</Button>
                </div>
            </header>

            <div className="dashboard-toolbar">
                <div className="toolbar-left">
                    <div className="dropdown-container">
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowFileDropdown(!showFileDropdown);
                                setShowViewDropdown(false);
                                setShowOrderStrategySubmenu(false);
                            }}
                            className="toolbar-button"
                        >
                            File
                        </button>
                        <div className={`dropdown-menu ${showFileDropdown ? 'show' : ''}`}>
                            <div className="dropdown-item">📄 New Chart</div>
                            <div className="dropdown-item">📁 Open Data Folder</div>
                            <div className="dropdown-item">💾 Save Template</div>
                            <div className="dropdown-item">�� Print Chart</div>
                            <div className="dropdown-item" onClick={logout} style={{color: '#ef4444', fontWeight: 600, borderTop: '1px solid rgba(6,182,212,0.1)'}}>🚪 Logout</div>
                        </div>
                    </div>

                    <div className="dropdown-container">
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowViewDropdown(!showViewDropdown);
                                setShowFileDropdown(false);
                                setShowOrderStrategySubmenu(false);
                            }}
                            className="toolbar-button"
                        >
                            View
                        </button>
                        <div className={`dropdown-menu ${showViewDropdown ? 'show' : ''}`}>
                            {(hasPermission('Dashboard.MarketWatchSection') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('market-watch');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>📊 Market Watch</span>
                                {panelStates['market-watch'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                            
                            {(hasPermission('Dashboard.MonitoringSection') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('monitoring');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>⚡ Monitoring</span>
                                {panelStates['monitoring'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                            
                            <div 
                                className="dropdown-item submenu-item"
                                onMouseEnter={() => {
                                    if (submenuHideTimeout.current) clearTimeout(submenuHideTimeout.current);
                                    setShowOrderStrategySubmenu(true);
                                }}
                                onMouseLeave={() => {
                                    submenuHideTimeout.current = setTimeout(() => setShowOrderStrategySubmenu(false), 200);
                                }}
                            >
                                <span>📋 Order/Strategy</span>
                                <span className="submenu-arrow">▶</span>
                                
                                <div 
                                    className={`dropdown-submenu ${showOrderStrategySubmenu ? 'show' : ''}`}
                                    onMouseEnter={() => {
                                        if (submenuHideTimeout.current) clearTimeout(submenuHideTimeout.current);
                                        setShowOrderStrategySubmenu(true);
                                    }}
                                    onMouseLeave={() => {
                                        submenuHideTimeout.current = setTimeout(() => setShowOrderStrategySubmenu(false), 200);
                                    }}
                                >
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('strategy');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>ArbX TRADER - GOLDARB</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'strategy' && <span className="checkmark">✓</span>}
                                    </div>
                                    {(hasPermission('Dashboard.QuickOrder') || isAdmin()) && (
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('quick-order');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>Quick Order</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'quick-order' && <span className="checkmark">✓</span>}
                                    </div>
                                    )}
                                    {(hasPermission('Dashboard.SpreadOrder') || isAdmin()) && (
                                    <div
                                        className="dropdown-item"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            showRightPanel('spread-order');
                                            setShowViewDropdown(false);
                                            setShowOrderStrategySubmenu(false);
                                        }}
                                    >
                                        <span>Spread Order</span>
                                        {panelStates['right-panel'] && currentRightPanel === 'spread-order' && <span className="checkmark">✓</span>}
                                    </div>
                                    )}
                                </div>
                            </div>
                            
                            {(hasPermission('Dashboard.TradePanel') || isAdmin()) && (
                            <div 
                                className="dropdown-item" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePanel('bottom-panel');
                                    setShowViewDropdown(false);
                                }}
                            >
                                <span>📈 Trade Panel</span>
                                {panelStates['bottom-panel'] && <span className="checkmark">✓</span>}
                            </div>
                            )}
                        </div>
                    </div>

                    <button
                        className="toolbar-button"
                        onClick={() => navigate('/algo-analytics')}
                        style={{
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)',
                            borderLeft: '2px solid rgba(59, 130, 246, 0.5)',
                            fontWeight: 600,
                            color: '#60a5fa'
                        }}
                        title="Open Multi-Strategy Analytics Dashboard"
                    >
                        📊 Analytics
                    </button>
                    <button className="toolbar-button">Charts</button>
                    <button className="toolbar-button">Tools</button>
                    <button className="toolbar-button">Window</button>
                </div>

                <div className="toolbar-icons">
                    <button className="icon-button">🔍</button>
                    <button className="icon-button">📊</button>
                    <button className="icon-button">📈</button>
                    {(() => {
                        const hasSettingsPermission = hasPermission('Settings') || isAdmin();
                        return hasSettingsPermission;
                    })() && (
                    <button className="icon-button" onClick={handleSettingsClick}><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></button>
                    )}
                    
                    
                </div>
            </div>


            <div className={`main-content${panelStates['bottom-panel'] ? ' has-bottom-panel' : ''}`}>
                {panelStates['market-watch'] && (hasPermission('Dashboard.MarketWatchSection') || isAdmin()) && (
                    <div className="panel market-watch-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>📊 Market Watch</span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('market-watch')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('market-watch')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['market-watch'] && (
                        <div className="panel-content">
                            <Box sx={{
                                '& .MuiTableContainer-root': {
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    marginTop: 0
                                },
                                '& .MuiPaper-root': {
                                    backgroundColor: 'transparent',
                                    color: 'white'
                                },
                                '& .MuiTableCell-root': {
                                    color: 'white',
                                    borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                    fontSize: '0.75rem'
                                },
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                    fontWeight: 'bold'
                                },
                                '& .MuiTableRow-root:hover': {
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)'
                                }
                            }}>
                                <MarketWatch onSymbolSelect={handleSymbolSelect} />
                            </Box>
                        </div>
                        )}
                    </div>
                )}
                
                {panelStates['monitoring'] && (hasPermission('Dashboard.MonitoringSection') || isAdmin()) && (
                    <div className="panel monitoring-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>⚡ Monitoring</span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('monitoring')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('monitoring')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['monitoring'] && (
                            <div className="panel-content">
                                <Monitoring />
                            </div>
                        )}
                    </div>
                )}

                {panelStates['right-panel'] && ((hasPermission('Dashboard.QuickOrder') || hasPermission('Dashboard.SpreadOrder')) || isAdmin()) && (
                    <div className="panel right-panel resizable-panel">
                        <div className="panel-header slim-header">
                            <span>
                                {currentRightPanel === 'quick-order' && '📝 Quick Order'}
                                {currentRightPanel === 'spread-order' && '📋 Spread Order'}
                                {currentRightPanel === 'strategy' && '📊 ArbX TRADER - GOLDARB'}
                            </span>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('right-panel')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small"><SettingsIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('right-panel')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['right-panel'] && (
                        <div className="panel-content">
                            {currentRightPanel === 'quick-order' && (hasPermission('Dashboard.QuickOrder') || isAdmin()) && <QuickOrder selectedSymbol={selectedSymbol} selectedAccount={selectedAccount} />}
                            {currentRightPanel === 'spread-order' && (hasPermission('Dashboard.SpreadOrder') || isAdmin()) && <Trades selectedSymbol={selectedSymbol} selectedAccount={selectedAccount} />}
                            {currentRightPanel === 'strategy' && StrategyScreen}
                        </div>
                        )}
                    </div>
                )}
                {panelStates['bottom-panel'] && (hasPermission('Dashboard.TradePanel') || isAdmin()) && (
                    <div className="panel bottom-panel resizable-panel" style={{ height: `${bottomPanelHeight}px` }}>
                        <div 
                            className="resize-handle-horizontal"
                            onMouseDown={handleMouseDown}
                            style={{
                                position: 'absolute',
                                top: '-4px',
                                left: '0',
                                right: '0',
                                height: '8px',
                                cursor: 'ns-resize',
                                backgroundColor: 'transparent',
                                zIndex: 10,
                                borderTop: isResizing ? '2px solid #06b6d4' : '1px solid transparent'
                            }}
                        />
                        <div className="panel-header slim-header with-tabs">
                            <div className="header-tabs">
                                <Box 
                                    className="trading-tabs-container"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <OrderMonitor tabsOnly={true} selectedAccount={selectedAccount} />
                                </Box>
                            </div>
                            <div style={{display:'flex',gap:4}}>
                                <IconButton size="small" onClick={() => handleCollapse('bottom-panel')}>
                                    <ExpandMoreIcon fontSize="small" sx={{ color: '#e2f3f4' }} />
                                </IconButton>
                                <IconButton size="small" onClick={handleTradePanelExpand}><FullscreenIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                                <IconButton size="small" onClick={() => closePanel('bottom-panel')}><CloseIcon fontSize="small" sx={{ color: '#e2f3f4' }} /></IconButton>
                            </div>
                        </div>
                        {!collapsed['bottom-panel'] && (
                        <div className="panel-content">
                            <Box sx={{
                                backgroundColor: 'transparent',
                                color: 'white',
                                '& .MuiTableContainer-root': {
                                    backgroundColor: 'transparent'
                                },
                                '& .MuiTable-root': {
                                    '& .MuiTableCell-root': {
                                        color: 'white',
                                        borderBottom: '1px solid rgba(6, 182, 212, 0.1)',
                                        fontSize: '0.75rem'
                                    },
                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                        fontWeight: 'bold'
                                    }
                                }
                            }}>
                                <OrderMonitor contentOnly={true} selectedAccount={selectedAccount} />
                            </Box>
                        </div>
                        )}
                    </div>
                )}
            </div>
            <div className="status-bar">
                <span style={{marginRight:24}}>Balance: $100,000.00</span>
                <span style={{marginRight:24}}>Equity: $99,800.00</span>
                <span style={{marginRight:24}}>Margin: $1,200.00</span>
                <span style={{marginRight:24}}>Free Margin: $98,600.00</span>
                <span>Margin Level: 8300%</span>
            </div>
            <ManualOrderClose open={manualCloseOpen} onClose={() => setManualCloseOpen(false)} />
            <SettingsMain open={showSettings} onClose={() => setShowSettings(false)} selectedAccount={selectedAccount} />
            
            {showTradePanelModal && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setShowTradePanelModal(false)}
                >
                    <Box
                        sx={{
                            width: '90%',
                            height: '90%',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '8px',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            position: 'relative',
                            zIndex: 10000
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{
                            padding: '8px 12px',
                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                            backgroundColor: '#0a0e1a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: '36px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <Typography sx={{ color: '#e2f3f4', fontWeight: 600, fontSize: '14px' }}>
                                Trade Panel - Expanded View
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowTradePanelModal(false)}
                                sx={{ color: '#e2f3f4' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <OrderMonitor selectedAccount={selectedAccount} />
                        </Box>
                    </Box>
                </Box>
            )}

            {showFullPreviewModal && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={() => setShowFullPreviewModal(false)}
                >
                    <Box
                        sx={{
                            width: '80%',
                            maxWidth: '900px',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '8px',
                            border: '1px solid rgba(6, 182, 212, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            position: 'relative',
                            zIndex: 10000,
                            maxHeight: '90vh'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{
                            padding: '12px 16px',
                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                            backgroundColor: '#0a0e1a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{ color: '#e2f3f4', fontWeight: 600, fontSize: '16px' }}>
                                Strategy Full Preview
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowFullPreviewModal(false)}
                                sx={{ color: '#e2f3f4' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            overflow: 'auto',
                            padding: '20px',
                            backgroundColor: '#1a1a1a',
                            color: 'white'
                        }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '40% 60%',
                                gap: 2
                            }}>
                            <Box sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                borderRadius: '8px',
                                padding: '16px'
                            }}>
                                <Typography sx={{
                                    color: '#06b6d4',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    mb: 2
                                }}>
                                    📊 BETA SPREAD CHART
                                </Typography>

                                <Box sx={{
                                    width: '100%',
                                    height: '300px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(6, 182, 212, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        padding: '20px 40px'
                                    }}>
                                        {[0, 1, 2, 3, 4].map((i) => (
                                            <Box key={i} sx={{
                                                width: '100%',
                                                height: '1px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                position: 'relative'
                                            }}>
                                                <Typography sx={{
                                                    position: 'absolute',
                                                    left: '-35px',
                                                    top: '-8px',
                                                    fontSize: '10px',
                                                    color: '#666'
                                                }}>
                                                    {(567.5 - i * 7.5).toFixed(1)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0 }}>
                                        <defs>
                                            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 0.3 }} />
                                                <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0 }} />
                                            </linearGradient>
                                        </defs>

                                        <path
                                            d="M 40,200 Q 200,180 350,190 T 820,210"
                                            stroke="#06b6d4"
                                            strokeWidth="2"
                                            fill="none"
                                        />

                                        <path
                                            d="M 40,200 Q 200,180 350,190 T 820,210 L 820,280 L 40,280 Z"
                                            fill="url(#chartGradient)"
                                        />

                                        <g>
                                            <circle cx="190" cy="185" r="4" fill="#f44336" />
                                            <path d="M 190,175 L 195,185 L 190,195 L 185,185 Z" fill="#f44336" />
                                            <text x="200" y="180" fontSize="10" fill="#f44336">Sell</text>
                                        </g>

                                        <g>
                                            <circle cx="520" cy="208" r="4" fill="#4caf50" />
                                            <path d="M 520,198 L 525,208 L 520,218 L 515,208 Z" fill="#4caf50" />
                                            <text x="530" y="212" fontSize="10" fill="#4caf50">Buy</text>
                                        </g>
                                    </svg>

                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: '5px',
                                        left: '40px',
                                        right: '40px',
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}>
                                        {['10:00', '10:10', '10:20', '10:30', '10:40'].map((time, i) => (
                                            <Typography key={i} sx={{ fontSize: '10px', color: '#666' }}>
                                                {time}
                                            </Typography>
                                        ))}
                                    </Box>

                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mt: 1,
                                    justifyContent: 'center'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#06b6d4' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Beta Spread</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#666', borderStyle: 'dashed' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Upper Bend</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 12, height: 2, backgroundColor: '#666', borderStyle: 'dashed' }} />
                                        <Typography sx={{ fontSize: '10px', color: '#bfc9db' }}>Lower Bend</Typography>
                                    </Box>
                                </Box>
                            </Box>

                                <Box sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                    border: '1px solid rgba(6, 182, 212, 0.2)',
                                    borderRadius: '8px',
                                    padding: '16px'
                                }}>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 3
                                    }}>
                                        <Box>
                                            <Typography sx={{
                                                color: '#06b6d4',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                mb: 2,
                                                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                                                pb: 1
                                            }}>
                                                Strategy Parameters
                                            </Typography>
                                            {/* FE-10: Use filteredStrategyData for UI display */}
                                            {[
                                                { label: 'Benchmark Spread:', value: filteredStrategyData?.BenchmarkSpread || '0.0' },
                                                { label: 'BUY Threshold:', value: filteredStrategyData?.BuyThreshold || '0.0' },
                                                { label: 'SELL Threshold:', value: filteredStrategyData?.SellThreshold || '0.0' },
                                                { label: 'Grid Threshold:', value: filteredStrategyData?.GridThreshold || '0.0' },
                                                { label: 'Leg TPS:', value: filteredStrategyData?.LegTPS || '0.0' },
                                                { label: 'Accumulative TPS:', value: filteredStrategyData?.AccumulativeTPS || '0.0' },
                                                { label: 'Accumulative TP:', value: filteredStrategyData?.AccumulativeTP || '0.0' },
                                                { label: 'Accumulative SLS:', value: filteredStrategyData?.AccumulativeSLS || '0.0' }
                                            ].map((field, index) => (
                                                <Box key={index} sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    py: 0.75,
                                                    borderBottom: index < 7 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                                                }}>
                                                    <Typography sx={{ fontSize: '13px', color: '#bfc9db' }}>
                                                        {field.label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>
                                                        {field.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Box>
                                            <Typography sx={{
                                                color: '#4caf50',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                mb: 2,
                                                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                                                pb: 1
                                            }}>
                                                Performance
                                            </Typography>
                                            {/* FE-10: Use filteredStrategyData for UI display */}
                                            {[
                                                { label: 'Total Trades:', value: filteredStrategyData?.TotalTrades?.toString() || '0' },
                                                { label: 'Win Rate:', value: filteredStrategyData?.WinRate ? `${filteredStrategyData.WinRate}%` : '0%' },
                                                { label: 'Profit Factor:', value: filteredStrategyData?.ProfitFactor?.toString() || '0.0' },
                                                { label: 'Sharpe Ratio:', value: filteredStrategyData?.SharpeRatio?.toString() || '0.0' },
                                                { label: 'Max Drawdown:', value: filteredStrategyData?.MaxDrawdown ? `${filteredStrategyData.MaxDrawdown}%` : '0%' },
                                                { label: 'Total Profit:', value: filteredStrategyData?.TotalProfit ? `${filteredStrategyData.TotalProfit >= 0 ? '+' : ''}$${filteredStrategyData.TotalProfit.toFixed(2)}` : '$0.00' }
                                            ].map((field, index) => (
                                                <Box key={index} sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    py: 0.75,
                                                    borderBottom: index < 5 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
                                                }}>
                                                    <Typography sx={{ fontSize: '13px', color: '#bfc9db' }}>
                                                        {field.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: '13px',
                                                        color: field.value.startsWith('+') ? '#4caf50' :
                                                               field.value.startsWith('-') ? '#f44336' : '#fff',
                                                        fontWeight: 600
                                                    }}>
                                                        {field.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default DashboardPage;